import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("auth_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
    } catch (error) {
      console.error("Auth error:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem("auth_token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    return response.data;
  };

  const logout = async () => {
    try {
      // Invalidate the server-side session before clearing local state
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Non-blocking: proceed with local cleanup even if the call fails
    }
    localStorage.removeItem("auth_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === "admin" || user?.is_admin === true;
  const isAuthenticated = () => !!user;
  
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      return response.data.user;
    } catch (e) {
      return null;
    }
  };
  
  // Permission helpers
  const canViewPayments = () => user?.is_admin || user?.can_view_payments === true;
  const canViewReports = () => user?.is_admin || user?.can_view_reports !== false;
  const canViewAnalytics = () => user?.is_admin || user?.can_view_analytics !== false;
  const canDeleteRecords = () => user?.is_admin || user?.can_delete_records === true;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      refreshUser,
      setUser,
      isAdmin,
      isAuthenticated,
      canViewPayments,
      canViewReports,
      canViewAnalytics,
      canDeleteRecords
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
